/**
 * Shared display helpers for learning path day content.
 * Ensures we show goal/day descriptions instead of generic labels (Goal 1, Day 1) everywhere.
 */

type DayLike = Record<string, unknown> & {
  title?: string;
  description?: string;
  learningObjectives?: string[];
  dayNumber?: number;
  day?: number;
};

/** Strip "Learning Journey:" prefix from text to avoid duplication. */
export function stripLearningJourneyPrefix(s: string): string {
  const t = s.trim();
  const prefix = /^Learning\s+Journey:\s*/i;
  return t.replace(prefix, '').trim() || t;
}

/** Whether a label looks generic — prefer description in those cases. */
export function isGenericLabel(s: string): boolean {
  const t = stripLearningJourneyPrefix(s).trim();
  if (!t) return true;
  if (/^Goal\s*\d+$/i.test(t) || /^Day\s*\d+$/i.test(t) || /^Module\s*\d+$/i.test(t) || /^Step\s*\d+$/i.test(t)) return true;
  if (/^(Goal\s*\d+(?:\s*[,→]\s*Goal\s*\d+)*)$/i.test(t)) return true;
  return false;
}

/** Keys to try for title (order matters). */
const TITLE_KEYS = [
  'title', 'lessonTitle', 'lesson_title', 'name', 'goalTitle', 'goal_title',
  'label', 'heading', 'goal', 'day_goal', 'objective', 'outcome', 'focus',
  'summary', 'content', 'text', 'description', 'goalDescription', 'goal_description',
  'dayDescription', 'day_description', 'lessonDescription', 'lesson_description',
];

/** Keys to try for description (order matters). */
const DESCRIPTION_KEYS = [
  'description', 'goalDescription', 'goal_description', 'dayDescription', 'day_description',
  'lessonDescription', 'lesson_description', 'lessonTitle', 'lesson_title',
  'summary', 'content', 'objective', 'outcome', 'focus', 'goal', 'day_goal',
  'name', 'text', 'title', 'label', 'heading',
];

/** Looks like an ID (UUID/long hex) — not useful as display text. */
function looksLikeId(s: string): boolean {
  if (!s || s.length < 10) return false;
  return /^[0-9a-f-]{20,}$/i.test(s) || /^[0-9a-f]{24}$/i.test(s);
}

/** First non-empty string from day using the given key list. */
function firstStringFrom(raw: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = raw[k];
    if (typeof v === 'string') {
      const t = v.trim();
      if (t.length > 0 && !looksLikeId(t)) return t;
    }
  }
  return undefined;
}

/** Extract title from day; checks many API key variants. */
function getTitle(day: DayLike): string | undefined {
  const raw = day as Record<string, unknown>;
  return firstStringFrom(raw, TITLE_KEYS);
}

/** Extract description from day; checks multiple API key variants (camelCase and snake_case). */
function getDescription(day: DayLike): string | undefined {
  const raw = day as Record<string, unknown>;
  const desc = firstStringFrom(raw, DESCRIPTION_KEYS);
  if (desc) return desc;
  const objs =
    day.learningObjectives ??
    raw.learning_objectives ??
    raw.objectives ??
    raw.goals;
  const first = Array.isArray(objs) && objs[0] ? String(objs[0]).trim() : undefined;
  return first && !looksLikeId(first) ? first : undefined;
}

/** Scan raw day for any string that looks like a human-readable label (fallback when title/description are generic). */
function getAnyDisplayText(day: DayLike): string | undefined {
  const raw = day as Record<string, unknown>;
  const seen = new Set<string>();
  for (const v of Object.values(raw)) {
    if (typeof v === 'string') {
      const t = v.trim();
      if (t.length >= 4 && t.length <= 200 && !looksLikeId(t) && !/^\d+$/.test(t) && !seen.has(t)) {
        seen.add(t);
        if (!isGenericLabel(t)) return t;
      }
    }
  }
  return undefined;
}

/**
 * Returns the display label for a learning path day.
 * When title is generic (Goal 1, Day 1, etc.), prefers description or first learningObjective.
 * @param day - day object (from dailyContents or normalized module)
 * @param dayNumber - fallback day index when title/description are missing
 * @param maxLength - optional truncation length (default 60)
 */
export function getLearningPathDayDisplayLabel(
  day: DayLike | null | undefined,
  dayNumber?: number,
  maxLength = 60
): string {
  if (!day) return dayNumber != null ? `Day ${dayNumber}` : 'Day 1';
  const dayNum = dayNumber ?? (day.dayNumber ?? day.day ?? 1) as number;
  const title = getTitle(day) ?? (typeof (day as Record<string, unknown>).title === 'string' ? (day as Record<string, unknown>).title as string : undefined);
  const fallbackTitle = title?.trim() || (dayNumber != null ? `Day ${dayNumber}` : `Day ${dayNum}`);
  const description = getDescription(day);
  if (description && isGenericLabel(fallbackTitle)) {
    const out = description.length > maxLength ? `${description.slice(0, maxLength - 3).trim()}…` : description;
    return out;
  }
  if (!isGenericLabel(fallbackTitle)) {
    return fallbackTitle.length > maxLength ? `${fallbackTitle.slice(0, maxLength - 3).trim()}…` : fallbackTitle;
  }
  const anyText = getAnyDisplayText(day);
  if (anyText) {
    return anyText.length > maxLength ? `${anyText.slice(0, maxLength - 3).trim()}…` : anyText;
  }
  return dayNumber != null ? `Day ${dayNumber}` : `Day ${dayNum}`;
}
